import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import $ from "jquery";
import IndoNepalTransaction from "./IndoNepalTransaction";
import {
    CheckCircle, Clock, AlertCircle, ExternalLink,
    Fingerprint, RefreshCw, Play, ArrowLeft,
} from "lucide-react";
import useApi from "../../../CustomHooks/useApi";
import useRDService from "../../../CustomHooks/useRDService";
import DeviceStatus from "../NewAeps/DeviceStatus";

const parsePidXml = (xml) => {
    const $xml = $(xml);
    const get  = (sel, attr) => $xml.find(sel).attr(attr) || "0";
    return {
        dc:         get("DeviceInfo", "dc"),
        ci:         get("Skey",       "ci"),
        hmac:       $xml.find("Hmac").text() || "0",
        dpId:       get("DeviceInfo", "dpId"),
        mc:         get("DeviceInfo", "mc"),
        sessionKey: $xml.find("Skey").text() || "0",
        mi:         get("DeviceInfo", "mi"),
        rdsId:      get("DeviceInfo", "rdsId"),
        rdsVer:     get("DeviceInfo", "rdsVer"),
        pidData:    $xml.find("Data").text() || "0",
    };
};

const STEPS = [
    { label: "Start"     },
    { label: "Register"  },
    { label: "Biometric" },
    { label: "Enroll"    },
    { label: "Consent"   },
    { label: "Done"      },
];

const STEP_IDX = {
    idle:              0,
    register:          1,
    initiate_ekyc:     2,
    biometric_pending: 2,
    polling:           2,
    enroll:            3,
    consent:           4,
    consent_polling:   4,
    ops_review:        4,
    done:              5,
};

const BACK_MAP = {
    register:          "idle",
    initiate_ekyc:     "idle",
    biometric_pending: "initiate_ekyc",
    enroll:            "biometric_pending",
    consent_polling:   "consent",
};

export default function IndoNepal() {
    const mid = useSelector((s) => s.auth.employeeDetails?.username);
    const { device, captureFingerprint, loading: rdLoading } = useRDService();

    const [screen,    setScreen]    = useState("idle");
    const [loading,   setLoading]   = useState(false);
    const [error,     setError]     = useState("");
    const [infoMsg,   setInfoMsg]   = useState("");

    const [otpProcessId, setOtpProcessId] = useState("");
    const [otp,          setOtp]          = useState("");
    const [consentUrl,   setConsentUrl]   = useState("");

    const [pollCount, setPollCount] = useState(0);
    const pollRef                   = useRef(null);
    const pollCountRef              = useRef(0);

    const { callApi: startApi }           = useApi("/indo-nepal/onboard/start",          "POST");
    const { callApi: registerApi }        = useApi("/indo-nepal/onboard/register",       "POST");
    const { callApi: initiateApi }        = useApi("/indo-nepal/onboard/initiate-ekyc",  "POST");
    const { callApi: pollApi }            = useApi("/indo-nepal/onboard/poll-ekyc",      "POST");
    const { callApi: enrollApi }          = useApi("/indo-nepal/onboard/enroll",         "POST");
    const { callApi: consentUrlApi }      = useApi("/indo-nepal/onboard/consent-url",    "GET");
    const { callApi: consentStatusApi }   = useApi("/indo-nepal/onboard/consent-status", "GET");

    const consentPollRef      = useRef(null);
    const consentPollCountRef = useRef(0);
    const [consentPollCount, setConsentPollCount] = useState(0);

    useEffect(() => () => { clearPoll(); clearConsentPoll(); }, []);

    const clearPoll = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };

    const clearConsentPoll = () => {
        if (consentPollRef.current) { clearInterval(consentPollRef.current); consentPollRef.current = null; }
    };

    const goBack = () => {
        if (screen === "polling") clearPoll();
        if (screen === "consent_polling") clearConsentPoll();
        const prev = BACK_MAP[screen];
        if (prev) { setError(""); setInfoMsg(""); setScreen(prev); }
    };

    const applyNextStep = (nextStep, data) => {
        setError(""); setInfoMsg("");
        switch (nextStep) {
            case "register":
                setOtpProcessId(data?.otpProcessId || "");
                setScreen("register");
                break;
            case "initiate_ekyc":
                setScreen("initiate_ekyc");
                break;
            case "consent": {
                const url = data?.consentUrl || "";
                setConsentUrl(url);
                if (url) window.open(url, "_blank");
                setScreen("consent");
                break;
            }
            case "ops_review":
                setInfoMsg(data?.agentConsentStatus?.Remarks || "PAN validation pending OPS review. TAT: 24 hours.");
                setScreen("ops_review");
                break;
            case "done":
                setScreen("done");
                break;
            default:
                setInfoMsg(data?.message || "Status updated.");
        }
    };

    const handleStart = async () => {
        try {
            setLoading(true); setError(""); setInfoMsg("");
            const res = await startApi({ merchantId: mid });

            /* Must be a successful response to proceed at all */
            if (res?.success !== true) {
                setError(res?.message || res?.data?.message || "Failed to start onboarding.");
                return;
            }

            const nextStep = res?.data?.nextStep;

            /* For the "register" step, also require otpSent + a valid otpProcessId */
            if (nextStep === "register") {
                if (res?.data?.otpSent !== true || !res?.data?.otpProcessId) {
                    setError(res?.message || res?.data?.message || "OTP could not be sent. Please try again.");
                    return;
                }
            }

            applyNextStep(nextStep, res?.data);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Failed to start onboarding");
        } finally { setLoading(false); }
    };

    const handleRegister = async () => {
        if (!otp) { setError("Please enter the OTP."); return; }
        try {
            setLoading(true); setError("");
            const res = await registerApi({ merchantId: mid, otpProcessId, otp: Number(otp) });
            setOtp("");

            if (res?.success !== true || res?.data?.nextStep !== "initiate_ekyc") {
                setError(res?.message || res?.data?.message || "OTP verification failed. Please try again.");
                return;
            }

            applyNextStep(res?.data?.nextStep, res?.data);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Registration failed");
        } finally { setLoading(false); }
    };

    const handleInitiateEkyc = async () => {
        try {
            setLoading(true); setError("");
            const res = await initiateApi({ merchantId: mid });
            const url = res?.data?.redirectUrl;
            if (url) { window.open(url, "_blank"); setScreen("biometric_pending"); }
            else setError("No redirect URL received from server.");
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Failed to initiate eKYC");
        } finally { setLoading(false); }
    };

    const startPolling = () => {
        setPollCount(0);
        pollCountRef.current = 0;
        setScreen("polling");

        const tick = async () => {
            try {
                const res = await pollApi({ merchantId: mid });
                if (res?.data?.StatusCode === "1") {
                    clearPoll();
                    setScreen("enroll");
                    setInfoMsg("Biometric verified! Capture fingerprint to complete enrollment.");
                } else {
                    pollCountRef.current += 1;
                    setPollCount(pollCountRef.current);
                }
            } catch {
                pollCountRef.current += 1;
                setPollCount(pollCountRef.current);
            }
        };
        tick();
        pollRef.current = setInterval(tick, 7000);
    };

    const MAX_CONSENT_POLLS = 4;

    const startConsentPolling = () => {
        setConsentPollCount(0);
        consentPollCountRef.current = 0;
        setScreen("consent_polling");

        const tick = async () => {
            try {
                const res = await consentStatusApi(null, `mid=${mid}`);

                const isApproved =
                    res?.success === true &&
                    String(res?.data?.StatusCode) === "1" &&
                    res?.data?.BankApprovalStatus === "Approved" &&
                    res?.data?.PANValidationStatus === "Success";

                if (isApproved) {
                    clearConsentPoll();
                    const startRes = await startApi({ merchantId: mid });
                    applyNextStep(startRes?.data?.nextStep, startRes?.data);
                    return;
                }

                consentPollCountRef.current += 1;
                setConsentPollCount(consentPollCountRef.current);

                if (consentPollCountRef.current >= MAX_CONSENT_POLLS) {
                    clearConsentPoll();
                    setScreen("consent");
                    setError(res?.message || res?.data?.message || "Consent not approved. Please complete the consent and try again.");
                }
            } catch (err) {
                consentPollCountRef.current += 1;
                setConsentPollCount(consentPollCountRef.current);

                if (consentPollCountRef.current >= MAX_CONSENT_POLLS) {
                    clearConsentPoll();
                    setScreen("consent");
                    setError(err?.response?.data?.message || err?.message || "Consent status check failed. Please try again.");
                }
            }
        };
        tick();
        consentPollRef.current = setInterval(tick, 7000);
    };

    const handleEnroll = async () => {
        try {
            setLoading(true); setError("");
            setInfoMsg("Place finger on scanner...");
            const wadhValue = "TF/lfPuh1n4ZY1xizYpqikIBm+gv65r51MFNek4uwNw="
            const pidXml = await captureFingerprint(wadhValue);
            if (!pidXml || !pidXml.includes('errCode="0"')) {
                setInfoMsg(""); setError("Fingerprint capture failed. Please try again.");
                return;
            }
            setInfoMsg("Submitting enrollment data...");
            const p = parsePidXml(pidXml);
            const res = await enrollApi({
                merchantId:                     mid,
                encryptedPid:                   p.pidData,
                encryptedHmac:                  p.hmac,
                sessionKeyValue:                p.sessionKey,
                certificateIdentifier:          Number(p.ci) || 0,
                registeredDeviceServiceId:      p.rdsId,
                registeredDeviceServiceVersion: p.rdsVer,
                registeredDeviceProviderId:     p.dpId,
                registeredDeviceCode:           p.dc,
                registeredDeviceModelId:        p.mi,
                registeredDevicePublicKey:      p.mc,
            });
            setInfoMsg("");

            /* Server-side error: show errorMessage and reset to start */
            if (res?.data?.nextStep === "error") {
                clearPoll();
                clearConsentPoll();
                setOtpProcessId("");
                setOtp("");
                setConsentUrl("");
                setPollCount(0);
                setConsentPollCount(0);
                setError(res?.data?.errorMessage || res?.message || "Enrollment failed. Please try again.");
                setScreen("idle");
                return;
            }

            applyNextStep(res?.data?.nextStep, res?.data);
        } catch (err) {
            setInfoMsg(""); setError(err?.response?.data?.message || err?.message || "Enrollment failed");
        } finally { setLoading(false); }
    };

    const handleGetConsentUrl = async () => {
        try {
            setLoading(true); setError("");
            const res = await consentUrlApi(null, `mid=${mid}`);
            const url = res?.data?.consentUrl;
            if (url) { setConsentUrl(url); window.open(url, "_blank"); }
            else setError("No consent URL received.");
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Failed to get consent URL");
        } finally { setLoading(false); }
    };

    const handleRefresh = async () => {
        try {
            setLoading(true); setError("");
            const res = await startApi({ merchantId: mid });
            applyNextStep(res?.data?.nextStep, res?.data);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Status check failed");
        } finally { setLoading(false); }
    };

    if (screen === "done") return <IndoNepalTransaction />;

    const stepIdx   = STEP_IDX[screen] ?? 0;
    const canGoBack = !!BACK_MAP[screen] || screen === "polling";

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="w-full">

                {/* Title */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Indo Nepal CSP Onboarding</h1>
                    <p className="text-gray-500 mt-1 text-lg">One-time registration to activate your CSP account</p>
                </div>

                {/* ── Progress bar ── */}
                <div className="mb-10 px-4">
                    <div className="relative flex items-start justify-between">
                        <div className="absolute top-5 left-5 right-5 h-1 bg-gray-200 z-0" />
                        <div
                            className="absolute top-5 left-5 h-1 bg-blue-600 z-0 transition-all duration-500"
                            style={{ width: stepIdx === 0 ? "0%" : `${(stepIdx / (STEPS.length - 1)) * 100}%` }}
                        />
                        {STEPS.map((s, i) => (
                            <div key={i} className="flex flex-col items-center z-10">
                                <div className={`
                                    w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all
                                    ${i < stepIdx  ? "bg-blue-600 border-blue-600 text-white" : ""}
                                    ${i === stepIdx ? "bg-blue-600 border-blue-600 text-white ring-4 ring-blue-100" : ""}
                                    ${i > stepIdx  ? "bg-white border-gray-300 text-gray-400" : ""}
                                `}>
                                    {i < stepIdx ? <CheckCircle size={18} /> : i + 1}
                                </div>
                                <span className={`mt-2 text-sm font-semibold
                                    ${i <= stepIdx ? "text-blue-600" : "text-gray-400"}`}>
                                    {s.label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Main card ── */}
                <div className="w-full bg-white rounded-2xl shadow-sm border p-10">

                    {/* Back button */}
                    {canGoBack && (
                        <button
                            onClick={goBack}
                            className="flex items-center gap-2 text-gray-500 hover:text-blue-600 text-base font-medium mb-8 transition-colors"
                        >
                            <ArrowLeft size={20} /> Back
                        </button>
                    )}

                    {/* Error banner */}
                    {error && (
                        <div className="mb-8 p-5 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
                            <AlertCircle size={22} className="text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-red-600 text-lg">{error}</p>
                        </div>
                    )}

                    {/* Info banner */}
                    {infoMsg && screen !== "ops_review" && (
                        <div className="mb-8 p-5 rounded-xl bg-blue-50 border border-blue-200 text-blue-700 text-lg">
                            {infoMsg}
                        </div>
                    )}

                    {/* ════════ IDLE ════════ */}
                    {screen === "idle" && (
                        <div className="text-center py-6">
                            <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-8">
                                <Play size={44} className="text-blue-600 ml-2" />
                            </div>
                            <h2 className="text-3xl font-bold mb-3 text-gray-900">Start CSP Onboarding</h2>
                            <p className="text-gray-500 text-xl mb-10 leading-relaxed max-w-lg mx-auto">
                                We'll check your current registration status and guide you through each remaining step.
                            </p>
                            <button
                                onClick={handleStart}
                                disabled={loading}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5 rounded-xl text-xl font-semibold disabled:opacity-50 transition-colors"
                            >
                                {loading ? "Checking status..." : "Start Onboarding"}
                            </button>
                        </div>
                    )}

                    {/* ════════ REGISTER ════════ */}
                    {screen === "register" && (
                        <div>
                            <h2 className="text-3xl font-bold mb-3 text-gray-900">Verify OTP</h2>
                            <p className="text-gray-500 text-lg mb-8 leading-relaxed">
                                An OTP has been sent to your registered mobile number. Enter it below to create your CSP.
                            </p>
                            <label className="block text-lg font-semibold mb-3 text-gray-700">Enter OTP</label>
                            <input
                                type="text"
                                maxLength={6}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                placeholder="• • • • • •"
                                className="w-full border-2 rounded-xl px-6 py-5 text-4xl tracking-[0.6em] text-center font-mono focus:outline-none focus:border-blue-500 mb-8"
                            />
                            <button
                                onClick={handleRegister}
                                disabled={loading || otp.length < 4}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5 rounded-xl text-xl font-semibold disabled:opacity-50 transition-colors"
                            >
                                {loading ? "Verifying..." : "Verify & Continue"}
                            </button>
                        </div>
                    )}

                    {/* ════════ INITIATE EKYC ════════ */}
                    {screen === "initiate_ekyc" && (
                        <div className="text-center py-6">
                            <div className="w-24 h-24 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-8">
                                <ExternalLink size={44} className="text-indigo-600" />
                            </div>
                            <h2 className="text-3xl font-bold mb-3 text-gray-900">Biometric eKYC</h2>
                            <p className="text-gray-500 text-xl mb-3 leading-relaxed max-w-lg mx-auto">
                                You'll be redirected to RBL Bank's secure page to complete Aadhaar biometric verification.
                            </p>
                            <p className="text-gray-400 text-base mb-10">
                                After completing biometric on that page, return here and click "I've Completed".
                            </p>
                            <button
                                onClick={handleInitiateEkyc}
                                disabled={loading}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-5 rounded-xl text-xl font-semibold disabled:opacity-50 transition-colors flex items-center justify-center gap-3"
                            >
                                <ExternalLink size={24} />
                                {loading ? "Preparing..." : "Open Biometric Page"}
                            </button>
                        </div>
                    )}

                    {/* ════════ BIOMETRIC PENDING ════════ */}
                    {screen === "biometric_pending" && (
                        <div className="text-center py-6">
                            <div className="w-24 h-24 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-8">
                                <Clock size={44} className="text-yellow-500" />
                            </div>
                            <h2 className="text-3xl font-bold mb-3 text-gray-900">Complete Biometric on RBL Page</h2>
                            <p className="text-gray-500 text-xl mb-3 leading-relaxed max-w-lg mx-auto">
                                The RBL biometric page has opened in a new tab.
                            </p>
                            <p className="text-gray-500 text-lg mb-10 leading-relaxed max-w-lg mx-auto">
                                Complete your Aadhaar biometric and click <strong>"OK"</strong> on that page, then return here.
                            </p>
                            <button
                                onClick={startPolling}
                                className="w-full bg-green-600 hover:bg-green-700 text-white py-5 rounded-xl text-xl font-semibold transition-colors"
                            >
                                I've Completed Biometric →
                            </button>
                        </div>
                    )}

                    {/* ════════ POLLING ════════ */}
                    {screen === "polling" && (
                        <div className="text-center py-6">
                            <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-8">
                                <RefreshCw size={44} className="text-blue-600 animate-spin" />
                            </div>
                            <h2 className="text-3xl font-bold mb-3 text-gray-900">Verifying Biometric Status</h2>
                            <p className="text-gray-500 text-xl mb-2">Checking RBL for biometric completion...</p>
                            <p className="text-gray-400 text-base mt-2">
                                Attempt {pollCount + 1} &nbsp;·&nbsp; Checking every 7 seconds
                            </p>
                        </div>
                    )}

                    {/* ════════ ENROLL ════════ */}
                    {screen === "enroll" && (
                        <div className="text-center py-6">
                            <DeviceStatus device={device} />
                            <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-8 mt-6">
                                <Fingerprint size={44} className="text-green-600" />
                            </div>
                            <h2 className="text-3xl font-bold mb-3 text-gray-900">Fingerprint Enrollment</h2>
                            <p className="text-gray-500 text-xl mb-10 leading-relaxed max-w-lg mx-auto">
                                Place your finger on the RD device scanner to capture biometric data and complete enrollment.
                            </p>
                            <button
                                onClick={handleEnroll}
                                disabled={loading || rdLoading}
                                className="w-full bg-green-600 hover:bg-green-700 text-white py-5 rounded-xl text-xl font-semibold disabled:opacity-50 transition-colors flex items-center justify-center gap-3"
                            >
                                <Fingerprint size={26} />
                                {loading || rdLoading ? "Processing..." : "Capture & Enroll"}
                            </button>
                        </div>
                    )}

                    {/* ════════ CONSENT ════════ */}
                    {screen === "consent" && (
                        <div className="text-center py-6">
                            <div className="w-24 h-24 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-8">
                                <CheckCircle size={44} className="text-purple-600" />
                            </div>
                            <h2 className="text-3xl font-bold mb-3 text-gray-900">OTP Consent Approval</h2>
                            <p className="text-gray-500 text-xl mb-10 leading-relaxed max-w-lg mx-auto">
                                Almost done! The agent must complete OTP consent to finalise onboarding.
                                Get the consent URL and share it with the agent.
                            </p>

                            {consentUrl ? (
                                <>
                                    <div className="p-4 bg-gray-50 rounded-xl border text-sm text-gray-600 break-all text-left mb-6">
                                        {consentUrl}
                                    </div>
                                    <button
                                        onClick={() => window.open(consentUrl, "_blank")}
                                        className="w-full bg-purple-600 hover:bg-purple-700 text-white py-5 rounded-xl text-xl font-semibold transition-colors mb-4 flex items-center justify-center gap-3"
                                    >
                                        <ExternalLink size={24} /> Open Consent URL Again
                                    </button>
                                    <button
                                        onClick={startConsentPolling}
                                        className="w-full bg-green-600 hover:bg-green-700 text-white py-5 rounded-xl text-xl font-semibold transition-colors"
                                    >
                                        I've Completed Consent →
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={handleGetConsentUrl}
                                    disabled={loading}
                                    className="w-full bg-purple-600 hover:bg-purple-700 text-white py-5 rounded-xl text-xl font-semibold disabled:opacity-50 transition-colors"
                                >
                                    {loading ? "Fetching URL..." : "Get Consent URL"}
                                </button>
                            )}
                        </div>
                    )}

                    {/* ════════ CONSENT POLLING ════════ */}
                    {screen === "consent_polling" && (
                        <div className="text-center py-6">
                            <div className="w-24 h-24 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-8">
                                <RefreshCw size={44} className="text-purple-600 animate-spin" />
                            </div>
                            <h2 className="text-3xl font-bold mb-3 text-gray-900">Verifying Consent Status</h2>
                            <p className="text-gray-500 text-xl mb-2">Checking if OTP consent is approved on Prabhu's side...</p>
                            <p className="text-gray-400 text-base mt-2">
                                Attempt {consentPollCount + 1} &nbsp;·&nbsp; Checking every 7 seconds
                            </p>
                        </div>
                    )}

                    {/* ════════ OPS REVIEW ════════ */}
                    {screen === "ops_review" && (
                        <div className="text-center py-6">
                            <div className="w-24 h-24 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-8">
                                <Clock size={44} className="text-amber-500" />
                            </div>
                            <h2 className="text-3xl font-bold mb-3 text-gray-900">Pending OPS Review</h2>
                            <p className="text-gray-500 text-xl mb-6 leading-relaxed max-w-lg mx-auto">
                                PAN validation requires manual review by RBL's OPS team.
                            </p>
                            <div className="p-6 bg-amber-50 rounded-xl border border-amber-200 text-amber-700 text-left mb-10">
                                <p className="font-semibold text-lg mb-1">Status</p>
                                <p className="text-base">{infoMsg || "Verification pending with OPS team."}</p>
                                <p className="mt-3 text-sm text-amber-600">Estimated TAT: 24 hours</p>
                            </div>
                            <button
                                onClick={handleRefresh}
                                disabled={loading}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5 rounded-xl text-xl font-semibold disabled:opacity-50 transition-colors flex items-center justify-center gap-3"
                            >
                                <RefreshCw size={22} className={loading ? "animate-spin" : ""} />
                                {loading ? "Checking..." : "Refresh Status"}
                            </button>
                        </div>
                    )}

                    {/* ════════ DONE ════════ */}
                    {screen === "done" && (
                        <div className="text-center py-6">
                            <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-8">
                                <CheckCircle size={48} className="text-green-600" />
                            </div>
                            <h2 className="text-3xl font-bold mb-3 text-green-700">CSP Onboarding Complete!</h2>
                            <p className="text-gray-500 text-xl mb-10 leading-relaxed max-w-lg mx-auto">
                                Your CSP account is now active. You can begin processing Indo Nepal transactions.
                            </p>
                            <div className="grid grid-cols-3 gap-4">
                                {["eKYC Verified", "Onboarding Success", "OTP Consent Done"].map((t) => (
                                    <div key={t} className="p-5 bg-green-50 rounded-xl border border-green-200 text-green-700 font-semibold text-base">
                                        <CheckCircle size={22} className="mx-auto mb-2" />
                                        {t}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}
